import { reactive } from '@vue/composition-api';

import standardReportApi from '@/apis/externalReports/standard/standard_report';

import { ReportState, SearchState } from '@/models/externalReports/standard/standardMap';
import {
  deselectReportItems,
  convSearchParamTsTo,
  convReport,
  loadStandardMtxs,
} from '@/components/ExternalReports/Standard/utils';
import type { StandardReportExt } from '@/models/externalReports/standard/standardReport';
import { createInjection } from '@/lib/createInjection';

interface InjectedState {
  reportState: ReportState;
  searchState: SearchState;
}

export interface UseStandardReportsResult {
  reportState: ReportState;
  searchReports: () => Promise<void>;
  selectReport: (reportId: number) => Promise<void>;
  deselectReport: (reportId: number) => boolean;
  getSelectedReport: () => StandardReportExt | null;
  deselectSelectedReportPhotos: () => void;
  clearReports: () => void;
}

const { provide, inject } = createInjection<InjectedState>('standardMap/useStandardReports');

export function provideStandardReports(searchState: SearchState): InjectedState {
  const reportState = reactive<ReportState>({
    reports: [],
  });
  const injectedState = { reportState, searchState };
  provide(injectedState);
  return injectedState;
}

export function useStandardReports(defaultInjectedState?: InjectedState): UseStandardReportsResult {
  const injectedState = defaultInjectedState ?? inject();
  const reportState = injectedState.reportState;
  const searchState = injectedState.searchState;

  const searchReports = async(): Promise<void> => {
    const tsFrom = new Date(searchState.searchConds.dateFrom);
    tsFrom.setHours(0, 0, 0, 0);
    const reqParams = {
      ts_from: tsFrom,
      ts_to: convSearchParamTsTo(searchState.searchConds.dateTo),
      han_name: searchState.searchConds.hanName,
    };
    const standardReports = await standardReportApi.index(reqParams);
    reportState.reports = standardReports
      .map(report => {
        return convReport(report);
      });
  };

  const getSelectedReport = (): StandardReportExt | null => {
    return reportState.reports.find(e => e.isSelected) ?? null;
  };

  const selectReport = async(reportId: number): Promise<void> => {
    const prev = getSelectedReport();
    if (prev && prev.id !== reportId) {
      deselectReportItems(prev);
    }
    for (const report of reportState.reports) {
      report.isSelected = report.id === reportId;
      if (!report.isSelected) continue;

      if (!report.isMtxsLoaded) {
        await loadStandardMtxs([report]);
      }
      break;
    }
  };

  const deselectReport = (reportId: number): boolean => {
    const report = getSelectedReport();
    if (!report || report.id !== reportId) return false;

    report.isSelected = false;
    deselectReportItems(report);
    return true;
  };

  const deselectSelectedReportPhotos = (): void => {
    const report = getSelectedReport();
    if (!report) return;

    deselectReportItems(report);
  };

  const clearReports = (): void => {
    reportState.reports.forEach(report => {
      for (const photo of report.photosExt) {
        if (photo.savedImage) {
          URL.revokeObjectURL(photo.savedImage);
        }
      }
    });
    reportState.reports = [];
  };

  return {
    reportState,
    searchReports,
    getSelectedReport,
    selectReport,
    deselectReport,
    deselectSelectedReportPhotos,
    clearReports,
  };
}
